import ModalBody from "@Components/Feedback/Modal/Body";
import ModalHeader from "@Components/Feedback/Modal/Header";
import ModalTitle from "@Components/Feedback/Modal/Title";
import { GraphQLResponse } from 'graphql-request';
import { GraphQLError } from 'graphql/error/GraphQLError';
import React from "react";

// TODO: make dynamic
const basePath = '/home/juje/domains/juje.dev.juje.co/JujeCo';

const openPhpStorm = (file: string, line: number) => {
    return `phpstorm://open?file=${encodeURIComponent(file)}&line=${line}`;
};

export default function ErrorModal({ response }: { response: GraphQLResponse }) {
    return (
        <>
            <ModalHeader>
                <ModalTitle>
                    {typeof response.errors === 'undefined' ? 'Error' : 'GraphQL Error'}
                </ModalTitle>
            </ModalHeader>

            <ModalBody className="list-group list-group-flush p-0">
                {typeof response.errors === 'undefined' ? (
                    <ErrorDisplayer response={response} />
                ) : (
                    <GqlErrorDisplayer response={response} />
                )}
            </ModalBody>
        </>
    );
}

interface GqlBodyProps {
    message: string;
    file: string;
    line: number;
    trace: {
        class: string;
        function: string;
        line: number;
        file: string;
    }[];
}

function ErrorDisplayer({ response }: { response: GraphQLResponse }) {
    const body: GqlBodyProps = JSON.parse(response.body);

    return (
        <div className="p-3">
            <pre className="bg-gray-100 p-3">{body.message}</pre>

            {body.trace && (
                <>
                    <p className="mt-2">
                        <a href={openPhpStorm(body.file, body.line)}>
                            <strong>File:</strong> {body.file.replace(`${basePath}/`, '')}<br />
                            <strong>Line:</strong> {body.line}
                        </a>
                    </p>

                    <h2 className="mt-3 text-xl font-bold">Trace</h2>
                    {body.trace.map((trace, index) => (
                        <span key={index}>
                            <h3 className="text-lg mt-2 font-bold">
                                {index}.&nbsp;
                                {trace.class ? (
                                    <>{trace.class}::{trace.function}()</>
                                ) : (
                                    <>{trace.function}()</>
                                )}
                            </h3>

                            {trace.file && (
                                <p>
                                    <a href={openPhpStorm(trace.file, trace.line)}>
                                        {trace.file.replace(`${basePath}/`, '')}:<strong>{trace.line}</strong>
                                    </a>
                                </p>
                            )}
                        </span>
                    ))}
                </>
            )}
        </div>
    );
}

interface GqlErrorExtensions {
    debugMessage?: string;
    file?: string;
    line?: number;
    trace?: {
        file?: string;
        line?: number;
        call?: string;
        class?: string;
        function?: string;
    }[];
}

export function GqlErrorDisplayer({ response }: { response: GraphQLResponse }) {
    const errors: (
        GraphQLError & { extensions?: GqlErrorExtensions, reference?: string }
    )[] | undefined = response.errors;

    if (!errors) {
        return null;
    }

    return (
        <>
            {errors.map((error, key) => <div key={key} className="p-3">
                {error.extensions ? (
                    <>
                        {error.message && (
                            <h2 className="text-xl font-bold pb-3">
                                {error.message}
                            </h2>
                        )}

                        <pre className="bg-gray-100 p-3 whitespace-pre-wrap">
                            {error.extensions.debugMessage ?? error.message}
                        </pre>

                        {error.extensions.file && error.extensions.line && (
                            <p className="mt-2">
                                <a
                                    href={openPhpStorm(error.extensions.file, error.extensions.line)}
                                    className="text-black text-decoration-none"
                                >
                                    <strong>File:</strong> {error.extensions.file.replace(`${basePath}/`, '')}<br />
                                    <strong>Line:</strong> {error.extensions.line}
                                </a>
                            </p>
                        )}

                        {error.extensions.trace && (
                            <>
                                <h2 className="mt-3 text-xl font-bold">Trace</h2>

                                {error.extensions.trace.map((trace, key) => (
                                    <div key={key}>
                                        {trace.file && trace.line ? (
                                            <>
                                                <h3 className="text-lg mt-2 font-bold">
                                                    {++key}. {trace.call ? (
                                                        trace.call
                                                    ) : (
                                                        trace.class ? (
                                                            `${trace.class}::${trace.function}()`
                                                        ) : (
                                                            trace.function
                                                        )
                                                    )}
                                                </h3>

                                                <a href={openPhpStorm(trace.file, trace.line)}>
                                                    {trace.file.replace(`${basePath}/`, '')}:<strong>{trace.line}</strong>
                                                </a>
                                            </>
                                        ) : (
                                            trace.call ? (
                                                <strong>{++key}. {trace.call}</strong>
                                            ) : (
                                                trace.class ? (
                                                    <>{trace.class}::{trace.function}()</>
                                                ) : (
                                                    <>{trace.function}()</>
                                                )
                                            )
                                        )}
                                    </div>
                                ))}
                            </>
                        )}
                    </>
                ) : (
                    <>
                        <h2>{error.message}</h2>
                        {error.reference && (
                            <p className="mb-0">
                                Reference: <code>{error.reference}</code>
                            </p>
                        )}
                    </>
                )}
            </div>)}
        </>
    )
}
